import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import myconfig from '../Config';

const COLUMNS: { key: keyof Api.ExamRegistration | 'DepartmentName'; label: string }[] = [
  { key: 'Firstname',      label: 'Fornavn' },
  { key: 'Lastname',       label: 'Etternavn' },
  { key: 'DateOfBirth',    label: 'Fødselsdato' },
  { key: 'DepartmentName', label: 'Avdeling' },
  { key: 'ClassName',      label: 'Klasse' },
  { key: 'ParentEmail',    label: 'Forelder e-post' },
  { key: 'ParentMobile',   label: 'Forelder mobil' },
  { key: 'Parent2Email',   label: 'Forelder 2 e-post' },
  { key: 'Parent2Mobile',  label: 'Forelder 2 mobil' },
  { key: 'DateCreated',    label: 'Registrert' },
];

const formatDob = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()} ${hh}:${mi}`;
};

const yearOf = (iso?: string): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.getFullYear();
};

const tableStyle: React.CSSProperties = { borderCollapse: 'collapse', fontSize: '0.875rem', width: '100%' };
const thStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px', background: '#f0f0f0', textAlign: 'left', position: 'sticky', top: 0 };
const tdStyle: React.CSSProperties = { border: '1px solid #ccc', padding: '6px 8px' };
const filterRowStyle: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' };
const filterLabelStyle: React.CSSProperties = { minWidth: 180, flex: '0 0 auto' };

const ExamRegistrationReportView: React.FC<RouteComponentProps> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<Api.ExamRegistration[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);

  const [deptFilter, setDeptFilter] = useState<string>('');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [classFilter, setClassFilter] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const [data, depts] = await Promise.all([
          RestService.get<Api.ExamRegistration[]>('/api/examregistration/All'),
          RestService.get<Api.Department[]>('/api/examregistration/GetDepartments'),
        ]);
        setRows(data ?? []);
        setDepartments(depts ?? []);
      } catch (e) {
        toast.error(`Kunne ikke laste rapport: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const departmentName = (id?: number) => departments.find(d => d.Id === id)?.Name ?? '';

  // Distinct filter options derived from the data so dropdowns never offer zero-result picks.
  const deptOptions = useMemo(() => {
    const ids = new Set(rows.map(r => r.DepartmentId).filter((v): v is number => v != null && v > 0));
    return departments
      .filter(d => ids.has(d.Id))
      .sort((a, b) => a.Name.localeCompare(b.Name, 'nb'));
  }, [rows, departments]);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    rows.forEach(r => {
      const y = yearOf(r.DateOfBirth);
      if (y != null) years.add(y);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [rows]);

  const classOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.ClassName) set.add(r.ClassName); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'nb', { numeric: true }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (deptFilter && String(r.DepartmentId ?? '') !== deptFilter) return false;
      if (yearFilter && String(yearOf(r.DateOfBirth) ?? '') !== yearFilter) return false;
      if (classFilter && (r.ClassName ?? '') !== classFilter) return false;
      return true;
    });
  }, [rows, deptFilter, yearFilter, classFilter]);

  const cellValue = (row: Api.ExamRegistration, key: typeof COLUMNS[number]['key']): string => {
    if (key === 'DepartmentName') return departmentName(row.DepartmentId);
    if (key === 'DateOfBirth') return formatDob(row.DateOfBirth);
    if (key === 'DateCreated') return formatDateTime(row.DateCreated);
    const v = (row as any)[key];
    return v == null ? '' : String(v);
  };

  const exportXlsx = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const token = localStorage.getItem('myToken');
      const response = await fetch(`${myconfig.rootUrl}/api/examregistration/ExportXlsx`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eksamensregistrering-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Kunne ikke eksportere: ${e}`);
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilter = !!(deptFilter || yearFilter || classFilter);
  const countLabel = hasActiveFilter ? `${filteredRows.length}/${rows.length}` : `${rows.length}`;

  return (
    <>
      <OKomBreadcrumb path='Rapporter, Eksamensregistrering'></OKomBreadcrumb>
      {isLoading && <OKomLoading text='Henter rapport...' />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <div className="ods-grid__column--12">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <h2 style={{ margin: 0 }}>Eksamensregistrering — rapport ({countLabel})</h2>
            <button
              type="button"
              className="ods-button"
              onClick={exportXlsx}
              disabled={rows.length === 0 || isExporting}
            >
              Eksporter til Excel
              {isExporting && (
                <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                  <BeatLoader color="#ffffff" size={8} />
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3">
          <div style={filterRowStyle}>
            <label className="ods-input__label" style={filterLabelStyle}>Avdeling
              <div className="ods-select__select">
                <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                  <option value="">Alle avdelinger</option>
                  {deptOptions.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                </select>
              </div>
            </label>

            <label className="ods-input__label" style={filterLabelStyle}>Fødselsår
              <div className="ods-select__select">
                <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                  <option value="">Alle år</option>
                  {yearOptions.map(y => (<option key={y} value={y}>{y}</option>))}
                </select>
              </div>
            </label>

            <label className="ods-input__label" style={filterLabelStyle}>Klasse
              <div className="ods-select__select">
                <select value={classFilter} onChange={e => setClassFilter(e.target.value)}>
                  <option value="">Alle klasser</option>
                  {classOptions.map(c => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
            </label>

            {hasActiveFilter && (
              <button
                type="button"
                className="ods-button ods-button--red"
                onClick={() => { setDeptFilter(''); setYearFilter(''); setClassFilter(''); }}
              >
                Nullstill filter
              </button>
            )}
          </div>
        </div>

        <div className="ods-grid__column--12 ods-margin-top-3" style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {COLUMNS.map(c => (<th key={c.key} style={thStyle}>{c.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => (
                <tr key={r.Id}>
                  {COLUMNS.map(c => (<td key={c.key} style={tdStyle}>{cellValue(r, c.key)}</td>))}
                </tr>
              ))}
              {filteredRows.length === 0 && !isLoading && (
                <tr>
                  <td style={tdStyle} colSpan={COLUMNS.length}>
                    {rows.length === 0 ? 'Ingen registreringer.' : 'Ingen treff for valgte filter.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default withRouter(ExamRegistrationReportView);
